import React, { ReactElement, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  getAgentStatistics,
  AgentStatisticsRow,
} from '@/services/OrganisationDashboardService'
import { useGlobalContext } from '@/context/GlobalContext'
import ExportReportShared from '@/components/UI/ExportReportShared/ExportReportShared'
import { getAgentFromStorage } from '@/utils/appUtils'
import { localDateTime } from '@/utils/formatUtils'
import { OVERVIEW } from '@/config'

interface AgentSetupCompletionProps {
  filtersData: any
}

export default function AgentSetupCompletion({
  filtersData,
}: AgentSetupCompletionProps): ReactElement {
  const { t } = useTranslation('translation')
  const { currentOrganisation } = useGlobalContext()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [sortModel, setSortModel] = useState<any[]>([])
  const agentId = getAgentFromStorage()

  // Move columns inside component so we can use `t` for translations and memoize for stability
  const columns = useMemo<GridColDef<AgentStatisticsRow>[]>(
    () => [
      {
        field: 'name',
        headerName: t('Agent Name'),
        width: 200,
        renderCell: params => {
          return (
            <Link
              to={`${OVERVIEW}?agent=${params.row._id}`}
              className="hover:tw-no-underline"
            >
              <Typography variant="body3Link" fontWeight={500}>
                {params.value}
              </Typography>
            </Link>
          )
        },
      },
      {
        field: 'persona',
        headerName: t('Agent Persona'),
        flex: 1.5,
        renderCell: params => {
          return (
            <Typography variant="body3" noWrap title={params.value}>
              {params.value}
            </Typography>
          )
        },
      },
      {
        field: 'user_count',
        headerName: t('Learners'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'document_count',
        headerName: t('Documents'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'quiz_count',
        headerName: t('Quizzes'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'roleplay_count',
        headerName: t('Role Plays'),
        flex: 1,
        type: 'number',
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: 'updated_at',
        headerName: t('Last Updated'),
        flex: 1,
        valueFormatter: (value?: string) => localDateTime(value),
      },
    ],
    [t],
  )

  const { data, isLoading } = useQuery({
    queryKey: [
      'org-agent-statistics',
      filtersData,
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
      agentId,
    ],
    queryFn: () => {
      const sortParams: any = {}
      if (sortModel.length > 0) {
        const sortField = sortModel[0].field
        const sortOrder = sortModel[0].sort === 'asc' ? 'asc' : 'dsc'
        sortParams.sort = `${sortField}::${sortOrder}`
      }

      return getAgentStatistics({
        ...filtersData,
        ...sortParams,
        page: paginationModel.page + 1, // API is 1-indexed, DataGrid is 0-indexed
        page_size: paginationModel.pageSize,
      })
    },
    enabled:
      !!currentOrganisation?._id &&
      !!(filtersData.filter_from_date && filtersData.filter_to_date),
  })

  // Use ref to maintain row count during loading to prevent DataGrid flickering
  const rowCountRef = React.useRef(data?.pagination.total_items || 0)

  const rowCount = React.useMemo(() => {
    if (data?.pagination.total_items !== undefined) {
      rowCountRef.current = data.pagination.total_items
    }
    return rowCountRef.current
  }, [data?.pagination.total_items])

  const handleSortModelChange = (newSortModel: any) => {
    setSortModel([...newSortModel])
  }

  return (
    <div className="tw-border tw-rounded-2xl tw-shadow-md tw-p-8">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <Typography variant="subtitle2" component="h2">
          {t('Agent Setup Completion')}
        </Typography>

        {!isLoading && (
          <ExportReportShared
            exportName="Agent-Setup-Completion"
            name="org-agent-statistics"
            filtersData={{
              ...filtersData,
            }}
          />
        )}
      </div>
      <Box sx={{ height: 550, width: '100%' }}>
        <DataGrid
          getRowId={row => row._id}
          rows={data?.rows || []}
          columns={columns}
          loading={isLoading}
          rowCount={rowCount}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell': {
              fontSize: '0.875rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f9fafb',
              fontSize: '0.875rem',
              fontWeight: 600,
            },
          }}
        />
      </Box>
    </div>
  )
}
